"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EmptySigner = exports.PrimarySigner = exports.BLSSigner = exports.ProofOfPossession = exports.SupernetAuth = exports.Validator = void 0;
const utils_1 = require("../../utils");
const builder_1 = require("../builder");
const constants_1 = require("../constants");
const signature_1 = require("../signature");
const types_1 = require("../types");
class Validator {
    nodeId;
    startTime;
    endTime;
    weight;
    constructor(nodeId, stakePeriod, weight) {
        this.nodeId = nodeId;
        // Since Durango upgrade only end time value is used to calculate stake period,
        // we use a fixed start time of now() as another would not have any effect anyways.
        this.startTime = utils_1.TimeUtils.now();
        this.endTime = this.startTime + stakePeriod;
        this.weight = weight;
    }
    getStakePeriod() {
        return this.endTime - this.startTime;
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(constants_1.ValidatorSize);
        buffer.write(this.nodeId);
        buffer.writeUInt64(this.startTime);
        buffer.writeUInt64(this.endTime);
        buffer.writeUInt64(this.weight);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        const nodeId = new types_1.NodeId(reader.read(constants_1.NodeIdSize).toCB58());
        const startTime = reader.readUInt64();
        const endTime = reader.readUInt64();
        const validator = new Validator(nodeId, endTime - startTime, reader.readUInt64());
        validator.startTime = startTime;
        validator.endTime = endTime;
        return validator;
    }
}
exports.Validator = Validator;
class SupernetAuth extends signature_1.AbstractSignable {
    typeId = constants_1.SupernetAuthTypeId;
    addressIndices;
    rewardsOwner;
    constructor(addresses, rewardsOwner) {
        super();
        this.addressIndices = [];
        if (typeof rewardsOwner !== 'undefined') {
            this.addressIndices = (0, builder_1.getSignersIndices)(addresses, rewardsOwner.addresses);
            this.addressIndices.sort((a, b) => {
                return a - b;
            });
        }
        this.rewardsOwner = rewardsOwner;
    }
    getAddresses() {
        if (typeof this.rewardsOwner === 'undefined') {
            throw new utils_1.SignatureError('cannot get addresses of read only supernet auth');
        }
        const addresses = [];
        for (let i = 0; i < this.addressIndices.length; i++) {
            addresses.push(this.rewardsOwner.addresses[i]);
        }
        return addresses;
    }
    getThreshold() {
        if (typeof this.rewardsOwner === 'undefined') {
            throw new utils_1.SignatureError('cannot get threshold of read only supernet auth');
        }
        return this.rewardsOwner.threshold;
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(4 + 4 + this.addressIndices.length * 4);
        buffer.writeUInt32(this.typeId);
        buffer.writeUInt32(this.addressIndices.length);
        for (const indice of this.addressIndices) {
            buffer.writeUInt32(indice);
        }
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.SupernetAuthTypeId);
        const addressIndicesCount = reader.readUInt32();
        const indices = [];
        for (let i = 0; i < addressIndicesCount; i++) {
            const indice = reader.readUInt32();
            indices.push(indice);
        }
        const supernetAuth = new SupernetAuth([]);
        supernetAuth.addressIndices = indices;
        return supernetAuth;
    }
}
exports.SupernetAuth = SupernetAuth;
class ProofOfPossession {
    publicKey;
    signature;
    constructor(publicKey, signature) {
        this.publicKey = publicKey;
        this.signature = signature;
    }
    serialize() {
        const publicKeyBytes = this.publicKey.serialize();
        const signatureBytes = this.signature.serialize();
        const buffer = utils_1.JuneoBuffer.alloc(publicKeyBytes.length + signatureBytes.length);
        buffer.write(publicKeyBytes);
        buffer.write(signatureBytes);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        return new ProofOfPossession(new types_1.BLSPublicKey(reader.read(constants_1.BLSPublicKeySize).toHex()), new types_1.BLSSignature(reader.read(constants_1.BLSSignatureSize).toHex()));
    }
}
exports.ProofOfPossession = ProofOfPossession;
class BLSSigner {
    typeId;
    constructor(typeId) {
        this.typeId = typeId;
    }
    serialize() {
        const buffer = utils_1.JuneoBuffer.alloc(4);
        buffer.writeUInt32(this.typeId);
        return buffer;
    }
}
exports.BLSSigner = BLSSigner;
class PrimarySigner extends BLSSigner {
    signer;
    constructor(signer) {
        super(constants_1.PrimarySignerTypeId);
        this.signer = signer;
    }
    serialize() {
        const baseBytes = super.serialize();
        const signerBytes = this.signer.serialize();
        const buffer = utils_1.JuneoBuffer.alloc(baseBytes.length + signerBytes.length);
        buffer.write(baseBytes);
        buffer.write(signerBytes);
        return buffer;
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.PrimarySignerTypeId);
        return new PrimarySigner(ProofOfPossession.parse(reader.read(constants_1.ProofOfPossessionSize)));
    }
}
exports.PrimarySigner = PrimarySigner;
class EmptySigner extends BLSSigner {
    constructor() {
        super(constants_1.EmptySignerTypeId);
    }
    static parse(data) {
        const reader = utils_1.JuneoBuffer.from(data).createReader();
        reader.readAndVerifyTypeId(constants_1.EmptySignerTypeId);
        return new EmptySigner();
    }
}
exports.EmptySigner = EmptySigner;
